"use client";

import { SimpleFieldConfig } from "@/components/shared/UniversalCRMTable/types";
import { formatDate } from "@/lib/utils/crm/time-entry-helpers";

/**
 * Create a render function for the column
 */
export function createRenderFunction(field: SimpleFieldConfig, fieldName: string) {
  return (value: any, item: any) => {
    // Handle null/undefined values
    if (value === null || value === undefined) {
      return <span className="text-gray-400">-</span>;
    }

    switch (field.type) {
      case "boolean":
        return (
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
              value ? "bg-green-100 text-green-800" : "bg-card-100 text-gray-800"
            }`}
          >
            {value ? "Yes" : "No"}
          </span>
        );

      case "date":
        return <div className="text-sm text-gray-600">{formatDate(value)}</div>;

      case "datetime":
        return <div className="text-sm text-gray-600">{formatDate(value)}</div>;

      case "number":
        // Check if it's a currency field
        if (
          fieldName.includes("amount") ||
          fieldName.includes("price") ||
          fieldName.includes("cost") ||
          fieldName.includes("rate")
        ) {
          return <div className="font-mono text-green-700">${Number(value).toFixed(2)}</div>;
        }
        return (
          <div className="font-mono text-card-foreground">{Number(value).toLocaleString()}</div>
        );

      case "select":
        // Check if it's a status field with predefined colors
        if (fieldName.includes("status")) {
          const statusColors: Record<string, string> = {
            pending: "bg-yellow-100 text-yellow-800",
            in_progress: "bg-blue-100 text-blue-800",
            completed: "bg-green-100 text-green-800",
            cancelled: "bg-red-100 text-red-800",
            active: "bg-green-100 text-green-800",
            inactive: "bg-card-100 text-gray-800",
          };

          const color = statusColors[value] || "bg-card-100 text-gray-800";

          return (
            <span
              className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${color}`}
            >
              {value}
            </span>
          );
        }

        // Check if it's a priority field
        if (fieldName.includes("priority")) {
          const priorityColors: Record<string, string> = {
            low: "bg-card-100 text-gray-800",
            medium: "bg-yellow-100 text-yellow-800",
            high: "bg-orange-100 text-orange-800",
            critical: "bg-red-100 text-red-800",
          };

          const color = priorityColors[value] || "bg-card-100 text-gray-800";

          return (
            <span
              className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${color}`}
            >
              {value}
            </span>
          );
        }

        return <span className="text-sm text-card-foreground">{value}</span>;

      case "multi-user":
        if (Array.isArray(value) && value.length > 0) {
          return (
            <div className="flex flex-wrap gap-1">
              {value.slice(0, 2).map((userId: string, index: number) => (
                <span
                  key={index}
                  className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                >
                  User {index + 1}
                </span>
              ))}
              {value.length > 2 && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  +{value.length - 2}
                </span>
              )}
            </div>
          );
        }
        return <span className="text-gray-400">Unassigned</span>;

      case "tags":
        if (Array.isArray(value) && value.length > 0) {
          return (
            <div className="flex flex-wrap gap-1">
              {value.slice(0, 2).map((tag: string, index: number) => (
                <span
                  key={index}
                  className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-card-100 text-gray-800"
                >
                  {tag}
                </span>
              ))}
              {value.length > 2 && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-card-100 text-gray-800">
                  +{value.length - 2}
                </span>
              )}
            </div>
          );
        }
        return <span className="text-gray-400">-</span>;

      case "textarea":
        return (
          <div className="text-sm text-gray-600 truncate max-w-xs" title={value}>
            {value}
          </div>
        );

      default:
        return <span className="text-sm text-card-foreground">{String(value)}</span>;
    }
  };
}
